import { ArrowRight, FolderClosed, MessageSquare, Smartphone } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { routeToIntake } from '../lib/intakeRouting';

// Rebuilt per Visual Contract §10.2: expresses the proof-publishing burden, not
// generic deepfake / information-overload anxiety. Draws on the ratified billboard
// (storyboard_s2_billboard_v2) cyan-dominant logic.
const trappedProof: Array<{ label: string; icon: LucideIcon }> = [
  { label: 'Phone camera rolls', icon: Smartphone },
  { label: 'Job folders and notes', icon: FolderClosed },
  { label: 'Supplier and client messages', icon: MessageSquare },
];

export function ProofProblem() {
  return (
    <section id="proof-problem" className="relative w-full overflow-hidden bg-obsidian py-20 sm:py-28">
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1fr] lg:items-center lg:px-8">
        <div className="order-2 lg:order-1">
          <p className="text-sm font-black uppercase tracking-wide text-cyan">The proof problem</p>
          <h2 className="mt-4 text-4xl font-black leading-tight tracking-tight text-chalk sm:text-5xl">
            Doing nothing is very different from having nothing to do.
          </h2>
          <p className="mt-6 text-lg leading-8 text-ash">
            Your business does real work every week. But the proof of that work stays trapped — in
            phones, job folders, supplier messages, and stale websites. Buyers and AI answer engines
            never see it, so it never earns you presence.
          </p>

          <ul className="mt-8 grid gap-3 sm:grid-cols-3">
            {trappedProof.map((item) => {
              const Icon = item.icon;
              return (
                <li
                  key={item.label}
                  className="flex items-center gap-3 border border-cyan/20 bg-cyan/[0.04] px-4 py-3 text-sm font-semibold text-chalk"
                >
                  <Icon className="size-5 shrink-0 text-cyan" aria-hidden="true" />
                  {item.label}
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            onClick={() => routeToIntake({ intent: 'm2m', productHint: 'm2m_t1' })}
            className="mt-9 inline-flex h-12 items-center justify-center gap-2 bg-cyan px-6 text-sm font-black uppercase tracking-wide text-obsidian transition hover:bg-violet hover:text-chalk"
          >
            Free the proof
            <ArrowRight className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="order-1 lg:order-2">
          <img
            src="/assets/storyboard_s2_billboard_v2.webp"
            alt="A worker pushing a bright cyan panel into a laptop screen that reads: doing nothing is very different from having nothing to do."
            className="w-full select-none rounded-sm border border-white/10"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}
